/**
 * DevZone Service
 * Manages B2B API keys for shop integrations
 * Enables shop admins to create API keys for third-party apps
 */

import { apiClient } from '../api/client';

export interface CreateApiKeyRequest {
  name: string;
  description?: string;
  scopes: string[];
  allowedIps?: string[];
  allowedOrigins?: string[];
  expiresInDays?: number;
}

export interface ApiKeyResponse {
  keyId: string;
  shopId: string;
  name: string;
  scopes: string[];
  status: 'ACTIVE' | 'REVOKED' | 'SUSPENDED' | 'EXPIRED';
  secretKey?: string; // Only shown once at creation/rotation
  rateLimits?: {
    requestsPerMinute?: number;
    requestsPerHour?: number;
    requestsPerDay?: number;
    requestsPerMonth?: number;
  };
  usageStats?: {
    totalRequests?: number;
    lastUsedAt?: string;
  };
  createdAt: string;
  expiresAt?: string;
}

export interface ApiScope {
  [key: string]: string;
}

export interface UsageStats {
  period: 'daily' | 'hourly' | 'monthly';
  data: {
    timestamp: string;
    requests: number;
    bandwidth: number;
  }[];
}

class DevZoneService {
  private readonly baseUrl = '/api/devzone';

  /**
   * Create a new API key
   * The secret key is only shown once in the response
   */
  async createApiKey(request: CreateApiKeyRequest): Promise<{ data: ApiKeyResponse; success: boolean; message: string }> {
    console.log('🔑 [DevZone] Creating API key:', request.name);
    try {
      const response = await apiClient.post<{ data: ApiKeyResponse; success: boolean; message: string }>(
        `${this.baseUrl}/keys`,
        request
      );
      console.log('✅ [DevZone] API key created successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to create API key:', error);
      throw new Error(error.response?.data?.message || 'Failed to create API key');
    }
  }

  /**
   * List all API keys for the authenticated shop
   * Secret keys are not included in the response
   */
  async listApiKeys(): Promise<{ data: ApiKeyResponse[]; success: boolean; message: string }> {
    console.log('📋 [DevZone] Fetching API keys list');
    try {
      const response = await apiClient.get<{ data: ApiKeyResponse[]; success: boolean; message: string }>(
        `${this.baseUrl}/keys`
      );
      console.log(`✅ [DevZone] Fetched ${response.data.data?.length || 0} API keys`);
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to fetch API keys:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch API keys');
    }
  }

  /**
   * Get details of a specific API key
   */
  async getApiKey(keyId: string): Promise<{ data: ApiKeyResponse; success: boolean; message: string }> {
    console.log('🔍 [DevZone] Fetching API key details:', keyId);
    try {
      const response = await apiClient.get<{ data: ApiKeyResponse; success: boolean; message: string }>(
        `${this.baseUrl}/keys/${keyId}`
      );
      console.log('✅ [DevZone] API key details fetched');
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to fetch API key details:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch API key details');
    }
  }

  /**
   * Update an existing API key
   * Can update name, scopes, allowed IPs/origins
   */
  async updateApiKey(keyId: string, request: CreateApiKeyRequest): Promise<{ data: ApiKeyResponse; success: boolean; message: string }> {
    console.log('✏️ [DevZone] Updating API key:', keyId);
    try {
      const response = await apiClient.put<{ data: ApiKeyResponse; success: boolean; message: string }>(
        `${this.baseUrl}/keys/${keyId}`,
        request
      );
      console.log('✅ [DevZone] API key updated successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to update API key:', error);
      throw new Error(error.response?.data?.message || 'Failed to update API key');
    }
  }

  /**
   * Revoke an API key
   * Once revoked, the key cannot be used anymore
   */
  async revokeApiKey(keyId: string, reason?: string): Promise<{ success: boolean; message: string }> {
    console.log('🗑️ [DevZone] Revoking API key:', keyId);
    try {
      const url = reason ? `${this.baseUrl}/keys/${keyId}?reason=${encodeURIComponent(reason)}` : `${this.baseUrl}/keys/${keyId}`;
      const response = await apiClient.delete<{ success: boolean; message: string }>(url);
      console.log('✅ [DevZone] API key revoked successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to revoke API key:', error);
      throw new Error(error.response?.data?.message || 'Failed to revoke API key');
    }
  }

  /**
   * Rotate API key secret
   * Generates a new secret key. The new secret is shown only once in the response
   */
  async rotateApiKey(keyId: string): Promise<{ data: ApiKeyResponse; success: boolean; message: string }> {
    console.log('🔄 [DevZone] Rotating API key secret:', keyId);
    try {
      const response = await apiClient.post<{ data: ApiKeyResponse; success: boolean; message: string }>(
        `${this.baseUrl}/keys/${keyId}/rotate`
      );
      console.log('✅ [DevZone] API key secret rotated successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to rotate API key:', error);
      throw new Error(error.response?.data?.message || 'Failed to rotate API key');
    }
  }

  /**
   * Get available API scopes
   */
  async getAvailableScopes(): Promise<{ data: ApiScope; success: boolean }> {
    console.log('📑 [DevZone] Fetching available scopes');
    try {
      const response = await apiClient.get<{ data: ApiScope; success: boolean }>(
        `${this.baseUrl}/scopes`
      );
      console.log('✅ [DevZone] Available scopes fetched');
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to fetch scopes:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch available scopes');
    }
  }

  /**
   * Get usage statistics for an API key
   */
  async getUsageStats(keyId?: string, period: 'daily' | 'hourly' | 'monthly' = 'daily'): Promise<{ data: UsageStats; success: boolean }> {
    console.log('📊 [DevZone] Fetching usage statistics');
    try {
      const params = new URLSearchParams();
      if (keyId) params.append('keyId', keyId);
      params.append('period', period);

      const response = await apiClient.get<{ data: UsageStats; success: boolean }>(
        `${this.baseUrl}/usage?${params.toString()}`
      );
      console.log('✅ [DevZone] Usage statistics fetched');
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to fetch usage statistics:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch usage statistics');
    }
  }

  /**
   * Get real-time rate limit status
   */
  async getRateLimitStatus(keyId?: string): Promise<{ data: any; success: boolean }> {
    console.log('⚡ [DevZone] Fetching real-time rate limit status');
    try {
      const url = keyId ? `${this.baseUrl}/usage/realtime?keyId=${keyId}` : `${this.baseUrl}/usage/realtime`;
      const response = await apiClient.get<{ data: any; success: boolean }>(url);
      console.log('✅ [DevZone] Rate limit status fetched');
      return response.data;
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to fetch rate limit status:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch rate limit status');
    }
  }
}

export const devZoneService = new DevZoneService();

